import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageCircle, X, Send, Sparkles, ChevronDown, User as UserIcon,
  Book, Loader2, PenLine, Mic, MicOff, Volume2, VolumeX, Square,
  ThumbsUp, ThumbsDown, Check, Play,
} from "lucide-react";
import { api, formatError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/* ─────────────────────────────────────────────────────────────────
 * Module-level singleton audio guard.
 *
 * Every Morgan audio element (auto-play, "Read aloud", voice preview)
 * MUST register itself here before calling .play(). Registering a new
 * clip synchronously pauses whatever was previously playing so two
 * Morgan voices can NEVER overlap. Subscribers (widget header) also
 * get an is-speaking flag so the "Stop speaking" pill can render.
 * ────────────────────────────────────────────────────────────────*/
let _morganActiveAudio = null;
const _morganAudioSubs = new Set();
const _morganAbortControllers = new Set();

function _emitAudio(active) {
  _morganAudioSubs.forEach((cb) => { try { cb(active); } catch { /* noop */ } });
}
function morganSubscribeAudio(cb) {
  _morganAudioSubs.add(cb);
  return () => _morganAudioSubs.delete(cb);
}
function morganRegisterAudio(a) {
  if (_morganActiveAudio && _morganActiveAudio !== a) {
    try { _morganActiveAudio.pause(); } catch { /* noop */ }
    try { _morganActiveAudio.src = ""; } catch { /* noop */ }
  }
  _morganActiveAudio = a || null;
  _emitAudio(!!a);
}
function morganClearAudio(a) {
  // Only clear if the caller still owns the slot (avoid clobbering a
  // newer clip that took over the slot after we lost the race).
  if (!a || _morganActiveAudio === a) {
    _morganActiveAudio = null;
    _emitAudio(false);
  }
}
function morganStopAllAudio() {
  if (_morganActiveAudio) {
    try { _morganActiveAudio.pause(); } catch { /* noop */ }
    try { _morganActiveAudio.src = ""; } catch { /* noop */ }
    _morganActiveAudio = null;
  }
  _morganAbortControllers.forEach((c) => { try { c.abort(); } catch { /* noop */ } });
  _morganAbortControllers.clear();
  _emitAudio(false);
}
function morganTrackAbort(ctrl) {
  _morganAbortControllers.add(ctrl);
  return () => _morganAbortControllers.delete(ctrl);
}

/**
 * Morgan — the CPG Institute's conversational AI companion.
 *
 * One simple chatbox for everyone. All admin surfaces (persona /
 * voice tuning, Good/Bad review, voice-flag triage, KB classification)
 * live in dedicated pages under /admin, not inside the widget. Warren
 * tests Morgan here the same way a founder does.
 *
 * Endpoint: POST /api/account/assistant/chat — always the caller's own
 * account. Server-side tenant isolation is unchanged.
 */
export default function AssistantWidget() {
  const { user } = useAuth() || {};
  const role = String(user?.role || "").toLowerCase();
  // Reviewers (admin + super_admin) get an inline Good/Bad affordance
  // on each Morgan reply so they can flag quality without leaving the
  // chat. Ordinary members never see it.
  const isReviewer = ["admin", "super_admin"].includes(role);

  const [open, setOpen] = useState(false);
  const [account, setAccount] = useState(null);
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const inputRef  = useRef(null);

  // ── Voice input state (tap-to-record; nothing autoplays) ───────────
  // Follow the mission's "user must intentionally start recording"
  // rule: no continuous listening, hard-cap at 30 s, editable
  // transcript stays in the textarea for review before send.
  const [recording, setRecording]         = useState(false);
  const [transcribing, setTranscribing]   = useState(false);
  const [micError, setMicError]           = useState(null);
  const mediaRecorderRef                  = useRef(null);
  const chunksRef                         = useRef([]);
  const streamRef                         = useRef(null);
  const recStartedAtRef                   = useRef(0);
  const recTimeoutRef                     = useRef(null);
  const RECORD_MAX_MS                     = 30_000;

  // ── Auto-play voice on every Morgan reply (persisted per browser) ──
  // Users expect Morgan to *talk back* by default. We store the toggle
  // in localStorage so a mute survives page reloads, and only mark the
  // freshly-received reply with `_autoplay` (never thread history) so
  // reopening a conversation doesn't blast old answers.
  const [autoplayVoice, setAutoplayVoice] = useState(() => {
    try {
      const v = window.localStorage.getItem("morgan.autoplayVoice");
      return v === null ? true : v === "1";
    } catch { return true; }
  });
  useEffect(() => {
    try { window.localStorage.setItem("morgan.autoplayVoice", autoplayVoice ? "1" : "0"); } catch { /* ignore */ }
  }, [autoplayVoice]);

  // ── Voice roster ─────────────────────────────────────────────────
  // Founders pick one of 6 curated ElevenLabs voices from the header
  // dropdown. Chosen slug is persisted to the account record (so it
  // follows the user across devices) with a localStorage mirror for
  // instant reads before the /catalog fetch resolves. The
  // ElevenLabs voice IDs stay on the server — we only ever ship
  // slugs.
  const [voiceSlug, setVoiceSlug] = useState(() => {
    try { return window.localStorage.getItem("morgan.voiceSlug") || "warren"; }
    catch { return "warren"; }
  });
  useEffect(() => {
    try { window.localStorage.setItem("morgan.voiceSlug", voiceSlug); } catch { /* ignore */ }
  }, [voiceSlug]);
  const [voiceCatalog, setVoiceCatalog] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get("/account/assistant/voice/catalog")
      .then((r) => {
        if (cancelled) return;
        setVoiceCatalog(r?.data?.voices || []);
        // Prefer the server-persisted slug over the local cache so
        // the pick follows the founder across devices. Falls back to
        // whatever's in localStorage / initial state if the server
        // hasn't stored one yet.
        const saved = (r?.data?.selected || "").trim().toLowerCase();
        if (saved) setVoiceSlug(saved);
      })
      .catch(() => { /* leave empty — dropdown hides gracefully */ });
    return () => { cancelled = true; };
  }, []);
  // Persist every voice change back to the account record. Debounce
  // isn't needed — the picker is a single click, and the write is
  // idempotent so a duplicate PUT from React StrictMode is harmless.
  const savedVoiceRef = useRef(null);
  useEffect(() => {
    // Skip on first hydration (before we know what the server has).
    if (savedVoiceRef.current === null) {
      savedVoiceRef.current = voiceSlug;
      return;
    }
    if (savedVoiceRef.current === voiceSlug) return;
    savedVoiceRef.current = voiceSlug;
    api.put("/account/assistant/voice/preference", { voice: voiceSlug })
       .catch(() => { /* silent — localStorage still holds the value */ });
  }, [voiceSlug]);

  // ── Continuous voice-conversation mode ────────────────────────────
  // Turned on the first time the user taps the mic. While it's on, the
  // widget auto-re-arms the microphone as soon as Morgan finishes
  // speaking so the whole exchange feels like a phone call. Any of
  // these exit the mode:
  //   • user types into the composer (their hands are back on the
  //     keyboard — assume they want text mode)
  //   • user mutes voice output (no voice → no voice loop)
  //   • user closes the widget
  //   • user taps the mic while it's already recording (cancel)
  const [voiceMode, setVoiceMode] = useState(false);
  const voiceModeRef              = useRef(false);
  useEffect(() => { voiceModeRef.current = voiceMode; }, [voiceMode]);
  // startRecordRef exposes the mic starter to MessageBubble without
  // threading the whole function through props — the ref is stable.
  const startRecordRef            = useRef(null);

  // ── Is-any-Morgan-audio-playing flag (from module-level singleton) ─
  // Powers the header "Stop speaking" pill so the founder always has
  // one obvious kill-switch regardless of which bubble is speaking.
  const [morganSpeaking, setMorganSpeaking] = useState(false);
  useEffect(() => morganSubscribeAudio(setMorganSpeaking), []);

  // Any keyboard input into the composer → exit voice loop.
  useEffect(() => {
    if (voiceMode && input.trim().length > 0) setVoiceMode(false);
  }, [input, voiceMode]);
  // Muting voice output → no reason to keep re-arming the mic. Also
  // hard-stop any clip currently in the air so toggling to silent
  // takes effect instantly (no "let this one finish" grace).
  useEffect(() => {
    if (!autoplayVoice) {
      morganStopAllAudio();
      if (voiceMode) setVoiceMode(false);
    }
  }, [autoplayVoice, voiceMode]);
  // Closing the widget releases the mic and drops the loop. Any
  // Morgan audio still playing (or waiting to play) is killed too.
  useEffect(() => {
    if (!open) {
      morganStopAllAudio();
      if (voiceMode) setVoiceMode(false);
    }
  }, [open, voiceMode]);
  // Any time we leave voice mode, ensure the barge-in watcher is
  // torn down so the OS mic light doesn't linger.
  useEffect(() => {
    if (!voiceMode) stopBargeInWatcher();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceMode]);

  // Load account context and thread list when opening
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [a, t] = await Promise.all([
          api.get("/accounts/me"),
          api.get("/account/assistant/threads"),
        ]);
        if (cancelled) return;
        setAccount(a.data);
        setThreads(t.data?.threads || []);
      } catch (err) {
        if (!cancelled) setError(formatError(err));
      }
      // Autofocus the composer for keyboard users.
      setTimeout(() => inputRef.current?.focus(), 40);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Autoscroll on new message
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const openThread = async (threadId) => {
    // Switching threads must silence whatever Morgan was saying in
    // the previous thread. Otherwise you'd get thread-A audio bleeding
    // into thread-B's context.
    morganStopAllAudio();
    try {
      const r = await api.get(`/account/assistant/threads/${threadId}`);
      setActiveThread(r.data.thread);
      setMessages(r.data.messages || []);
      setError(null);
    } catch (err) {
      setError(formatError(err));
    }
  };

  const newThread = () => {
    // Kill any lingering audio from the previous thread so "New" is
    // a genuine fresh start.
    morganStopAllAudio();
    setActiveThread(null);
    setMessages([]);
    setError(null);
    setTimeout(() => inputRef.current?.focus(), 20);
  };

  // ── Voice: start/stop mic with Voice Activity Detection ─────────────
  //   Push-once conversation model: tap the mic once and the widget
  //   listens continuously. The Web Audio graph below computes short-
  //   window RMS on the raw mic stream; when we've *heard* speech and
  //   then measured `SILENCE_MS` of quiet, we auto-stop the recorder.
  //   Because `mr.onstop` transcribes + sends + auto-re-arms in voice
  //   mode, the entire loop runs without any further taps until the
  //   user hits "End call" (or types, mutes, or closes the widget).
  const VAD_SPEECH_RMS   = 0.020;   // mic level counted as "user is speaking"
  const VAD_SILENCE_RMS  = 0.010;   // mic level counted as "silent"
  const VAD_SILENCE_MS   = 1400;    // trailing quiet before we auto-stop
  const VAD_MIN_SPEECH_MS = 350;    // clip must contain ≥ this much speech
  const audioCtxRef       = useRef(null);
  const analyserRafRef    = useRef(0);

  const teardownVad = () => {
    if (analyserRafRef.current) {
      cancelAnimationFrame(analyserRafRef.current);
      analyserRafRef.current = 0;
    }
    try { audioCtxRef.current?.close(); } catch { /* noop */ }
    audioCtxRef.current = null;
  };

  const stopMedia = () => {
    try { mediaRecorderRef.current?.stop(); } catch { /* noop */ }
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    if (recTimeoutRef.current) { clearTimeout(recTimeoutRef.current); recTimeoutRef.current = null; }
    teardownVad();
    streamRef.current = null;
    mediaRecorderRef.current = null;
  };

  const startRecord = async () => {
    if (recording || transcribing) return;
    setMicError(null);
    // First mic tap of the session activates continuous voice mode so
    // the widget will auto-re-arm after Morgan finishes speaking.
    setVoiceMode(true);
    if (!navigator.mediaDevices?.getUserMedia) {
      setMicError("Voice input isn't supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus" : "audio/webm",
      });
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const durationMs = Math.min(RECORD_MAX_MS, Date.now() - recStartedAtRef.current);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        chunksRef.current = [];
        setRecording(false);
        // VAD may have stopped us with <1s of blob — Whisper often
        // returns hallucinated "you"/"thank you"/etc for pure silence.
        // Skip the trip and just re-arm.
        if (!blob || blob.size < 1500) {
          teardownVad();
          if (voiceModeRef.current) {
            setTimeout(() => { if (voiceModeRef.current) startRecordRef.current?.(); }, 250);
          }
          return;
        }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "clip.webm");
          fd.append("duration_ms", String(durationMs));
          const r = await api.post("/account/assistant/voice/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const text = (r.data?.transcript || "").trim();
          if (!text) {
            // Empty transcript in voice mode: don't block the loop —
            // silently re-arm and keep the call going.
            if (voiceModeRef.current) {
              setTimeout(() => { if (voiceModeRef.current) startRecordRef.current?.(); }, 250);
            } else {
              setMicError("I couldn't hear anything — try again.");
            }
          } else {
            // Hands-free voice-in / voice-out: send the transcript
            // straight through to Morgan without a review step. The
            // reply auto-plays via the `_autoplay` marker in `send`.
            // The user's spoken words still appear as their bubble in
            // the conversation, so nothing is invisible — just faster.
            send(null, text);
          }
        } catch (err) {
          setMicError(err?.response?.data?.detail || "Voice transcription failed — text chat still works.");
        } finally {
          setTranscribing(false);
        }
      };
      recStartedAtRef.current = Date.now();
      mr.start();
      setRecording(true);
      recTimeoutRef.current = setTimeout(stopMedia, RECORD_MAX_MS);

      // ── VAD loop ──────────────────────────────────────────────────
      // Every animation frame, compute RMS from the FFT time-domain
      // buffer. Once we've seen speech, keep watching for a trailing
      // window of `SILENCE_MS` at low RMS — then stop the recorder.
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        const ctx = new AC();
        audioCtxRef.current = ctx;
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        analyser.smoothingTimeConstant = 0.4;
        src.connect(analyser);
        const buf = new Float32Array(analyser.fftSize);

        let heardSpeechAt = 0;
        let quietSince    = 0;
        const tick = () => {
          if (!audioCtxRef.current) return;
          analyser.getFloatTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
          const rms = Math.sqrt(sum / buf.length);
          const now = Date.now();
          if (rms >= VAD_SPEECH_RMS) {
            if (!heardSpeechAt) heardSpeechAt = now;
            quietSince = 0;
          } else if (rms < VAD_SILENCE_RMS && heardSpeechAt) {
            if (!quietSince) quietSince = now;
            if (now - quietSince >= VAD_SILENCE_MS
                && (now - heardSpeechAt) >= VAD_MIN_SPEECH_MS) {
              // Trailing silence long enough — stop and process.
              analyserRafRef.current = 0;
              try { mediaRecorderRef.current?.stop(); } catch { /* noop */ }
              return;
            }
          }
          analyserRafRef.current = requestAnimationFrame(tick);
        };
        analyserRafRef.current = requestAnimationFrame(tick);
      } catch { /* audio-graph not fatal; fall back to manual stop / 30s cap */ }
    } catch (err) {
      const msg = err?.name === "NotAllowedError"
        ? "Microphone permission denied. Enable it in your browser."
        : "Couldn't access the microphone.";
      setMicError(msg);
    }
  };

  const cancelRecord = () => {
    // Discard current chunks; DON'T upload.
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = () => setRecording(false);
    }
    stopMedia();
    chunksRef.current = [];
    setRecording(false);
    setTranscribing(false);
    // Manual cancel = user wants out of the voice loop.
    setVoiceMode(false);
  };

  // ── Barge-in watcher (voice mode only) ─────────────────────────────
  //   While Morgan is speaking, we open a *listen-only* mic stream and
  //   watch the RMS level. If the user starts talking (level crosses
  //   the speech threshold) we synchronously stop the TTS audio and
  //   kick off a full recording so the next turn begins the moment
  //   they interrupt — a real interruption, not a wait-your-turn queue.
  const bargeAudioRef       = useRef(null);      // current TTS <audio>
  const bargeStreamRef      = useRef(null);
  const bargeCtxRef         = useRef(null);
  const bargeRafRef         = useRef(0);

  const stopBargeInWatcher = () => {
    if (bargeRafRef.current) { cancelAnimationFrame(bargeRafRef.current); bargeRafRef.current = 0; }
    try { bargeCtxRef.current?.close(); } catch { /* noop */ }
    try { bargeStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    bargeCtxRef.current    = null;
    bargeStreamRef.current = null;
    bargeAudioRef.current  = null;
  };

  const startBargeInWatcher = async (audioEl) => {
    // Only relevant while we're in a live voice conversation.
    if (!voiceModeRef.current || !audioEl) return;
    stopBargeInWatcher();
    bargeAudioRef.current = audioEl;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      bargeStreamRef.current = stream;
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      bargeCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.35;
      src.connect(analyser);
      const buf = new Float32Array(analyser.fftSize);
      // Barge-in sensitivity. Three presets so users can tune per
      // environment; default = "low" so ambient office noise
      // (keyboard, breathing, HVAC, another voice in the room) does
      // NOT cut Morgan off mid-sentence. Only sustained, near-mic
      // speech interrupts. Change via
      // `localStorage.morgan.bargeSensitivity = "medium" | "high"`.
      const preset = (typeof window !== "undefined"
        ? (window.localStorage?.getItem("morgan.bargeSensitivity") || "low")
        : "low").toLowerCase();
      const PRESETS = {
        low:    { rms: 0.18, frames: 12, gate: 750 },  // ~200 ms of confirmed speech; 750 ms warm-up
        medium: { rms: 0.12, frames: 8,  gate: 500 },
        high:   { rms: 0.06, frames: 5,  gate: 300 },
      };
      const { rms: BARGE_RMS, frames: HIT_FRAMES, gate: GATE_MS } =
        PRESETS[preset] || PRESETS.low;
      // Warm-up gate — during the first N ms of TTS playback we
      // ignore mic entirely so the assistant's own bleed-through
      // (even with echo-cancel) can never self-interrupt.
      const startedAt = performance.now();
      let hits = 0;
      const tick = () => {
        if (!bargeCtxRef.current) return;
        if (performance.now() - startedAt < GATE_MS) {
          bargeRafRef.current = requestAnimationFrame(tick);
          return;
        }
        analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
        const rms = Math.sqrt(sum / buf.length);
        // Require N consecutive over-threshold frames (~HIT_FRAMES *
        // 16 ms = 200 ms at "low") so a single cough / mouse click
        // never cuts Morgan off.
        hits = rms >= BARGE_RMS ? hits + 1 : 0;
        if (hits >= HIT_FRAMES) {
          try { bargeAudioRef.current?.pause(); } catch { /* noop */ }
          stopBargeInWatcher();
          setTimeout(() => { if (voiceModeRef.current) startRecordRef.current?.(); }, 60);
          return;
        }
        bargeRafRef.current = requestAnimationFrame(tick);
      };
      bargeRafRef.current = requestAnimationFrame(tick);
    } catch { /* mic denied → user can still let Morgan finish */ }
  };

  // Wire the ref so MessageBubble can call `startRecord()` when its
  // audio finishes, without needing us to lift the entire function.
  useEffect(() => { startRecordRef.current = startRecord; });
  const bargeStartRef = useRef(null);
  const bargeStopRef  = useRef(null);
  useEffect(() => { bargeStartRef.current = startBargeInWatcher; });
  useEffect(() => { bargeStopRef.current  = stopBargeInWatcher; });

  // Ensure stream is released on unmount so the browser mic
  // indicator doesn't linger after the widget closes.  Also kill
  // any Morgan audio that might still be in the air.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => { stopMedia(); morganStopAllAudio(); }, []);

  const send = async (evt, overrideText) => {
    evt?.preventDefault?.();
    // Voice-in path passes `overrideText` directly so the transcript
    // never has to touch the composer (no review step, no flicker).
    const text = (typeof overrideText === "string" ? overrideText : input).trim();
    if (!text || sending) return;
    // Sending a new turn = the previous reply is no longer relevant.
    // Cut any TTS still playing / in-flight so Morgan doesn't finish
    // yesterday's sentence over today's answer.
    morganStopAllAudio();
    setError(null);
    setSending(true);
    const optimistic = { id: `tmp-${Date.now()}`, role: "user", content: text };
    setMessages((prev) => [...prev, optimistic]);
    if (typeof overrideText !== "string") setInput("");
    try {
      // Everyone — members AND admins — chats through the member
      // endpoint so the widget stays one simple design. Admin
      // configuration lives at /admin/morgan-studio, review at
      // /admin/account-assistant-review.
      const r = await api.post("/account/assistant/chat", {
        thread_id: activeThread?.id || null,
        message: text,
      });
      const asst = {
        id: r.data?.assistant_message_id || `asst-${Date.now()}`,
        assistant_message_id: r.data?.assistant_message_id || null,
        role: "assistant",
        content: r.data?.answer || (r.data?.error === "provider_error"
          ? "Morgan is temporarily unavailable. Please try again shortly."
          : ""),
        citations: (r.data?.sources || []).map((s) => ({
          source: s.source, title: s.title,
          document_id: s.document_id, account_id: s.account_id,
          domain: s.domain, domain_name: s.domain_name,
        })),
        error: r.data?.error,
        // Marker consumed once by MessageBubble to trigger auto-play.
        // Never set on history rows fetched from the thread endpoint.
        _autoplay: true,
      };
      if (!activeThread && r.data?.thread_id) {
        setActiveThread({ id: r.data.thread_id, title: text.slice(0, 60) });
        try {
          const t = await api.get("/account/assistant/threads");
          setThreads(t.data?.threads || []);
        } catch { /* non-blocking */ }
      }
      setMessages((prev) => [...prev, asst]);
    } catch (err) {
      setError(formatError(err));
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      // Only restore the composer on typed-input failures. Voice-in
      // failures surface via the mic error banner instead.
      if (typeof overrideText !== "string") setInput(text);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        type="button"
        data-testid="assistant-launcher-btn"
        aria-label={open ? "Close Morgan assistant" : "Open Morgan assistant"}
        onClick={() => setOpen((v) => !v)}
        className="fixed z-40 bottom-5 right-5 md:bottom-6 md:right-6 h-14 w-14 rounded-full shadow-2xl border border-neutral-200 bg-gradient-to-br from-pink-500 to-fuchsia-600 text-white grid place-items-center hover:scale-105 transition-transform focus:outline-none focus:ring-4 focus:ring-pink-500/30"
      >
        {open ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="assistant-panel"
          role="dialog"
          aria-label="Morgan assistant"
          className="fixed z-40 bottom-24 right-3 md:right-6 w-[calc(100vw-1.5rem)] md:w-[380px] max-w-[400px] h-[min(80vh,640px)] bg-white rounded-2xl border border-neutral-200 shadow-2xl overflow-hidden flex flex-col animate-in fade-in slide-in-from-bottom-3 duration-150"
        >
          {/* Header — subtle magenta gradient over near-black. Matches
              the CPG Institute brand primary (hsl 328 88% 55%). */}
          <div className="relative px-3.5 py-3 bg-gradient-to-br from-neutral-950 via-neutral-900 to-fuchsia-950 text-white">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-pink-400 to-fuchsia-600 grid place-items-center shadow-[inset_0_1px_0_rgba(255,255,255,.25),0_2px_8px_rgba(219,39,119,.35)]">
                  <Sparkles className="h-3.5 w-3.5 text-white" strokeWidth={2.4} />
                </div>
                <div className="min-w-0 leading-tight">
                  <div className="text-[14px] font-semibold tracking-tight truncate flex items-center gap-1.5">
                    Morgan
                    {voiceMode && (
                      <span
                        data-testid="assistant-voice-mode-badge"
                        className="inline-flex items-center gap-1 rounded-full bg-pink-500/20 border border-pink-400/40 px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold text-pink-100"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-pink-300 animate-pulse" />
                        Live
                      </span>
                    )}
                  </div>
                  <div className="text-[10.5px] text-pink-200/80 truncate font-medium" data-testid="assistant-account-label">
                    CPG Institute
                    {account?.account?.name ? ` · ${account.account.name}` : ""}
                    {account?.membership?.role_in_account === "seat" ? " · Seat" : ""}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {morganSpeaking && (
                  <button
                    type="button"
                    onClick={morganStopAllAudio}
                    data-testid="assistant-stop-speaking"
                    title="Stop Morgan from speaking"
                    aria-label="Stop Morgan from speaking"
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full bg-white text-neutral-900 hover:bg-neutral-100 transition"
                  >
                    <Square className="h-3 w-3" fill="currentColor" /> Stop
                  </button>
                )}
                {voiceMode && (
                  <button
                    type="button"
                    onClick={() => {
                      // Stop any active recording so the mic light on
                      // the OS doesn't linger after ending the call.
                      if (recording) cancelRecord();
                      setVoiceMode(false);
                    }}
                    data-testid="assistant-end-voice-mode"
                    title="End voice conversation"
                    className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-100 transition"
                  >End call</button>
                )}
                <button
                  type="button"
                  onClick={() => setAutoplayVoice((v) => !v)}
                  data-testid="assistant-voice-toggle"
                  aria-pressed={autoplayVoice}
                  title={autoplayVoice ? "Voice is on — Morgan speaks each reply" : "Voice is off — replies are silent"}
                  className={`p-1.5 rounded-full transition ${autoplayVoice ? "bg-white/15 hover:bg-white/25 text-white" : "bg-white/5 hover:bg-white/10 text-white/50"}`}
                >
                  {autoplayVoice
                    ? <Volume2 className="h-3.5 w-3.5" />
                    : <VolumeX className="h-3.5 w-3.5" />}
                </button>
                {/* Voice-picker: custom popover with per-row Play-sample
                    buttons so founders can preview each option before
                    committing. Only renders once the roster has
                    loaded so a network hiccup doesn't leave an empty
                    picker. */}
                {voiceCatalog.length > 0 && (
                  <VoicePicker
                    catalog={voiceCatalog}
                    value={voiceSlug}
                    onChange={setVoiceSlug}
                    disabled={!autoplayVoice}
                  />
                )}
                <button
                  type="button"
                  onClick={newThread}
                  data-testid="assistant-new-thread-btn"
                  title="Start a new conversation"
                  className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full bg-white/10 hover:bg-white/20 transition"
                >New</button>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => setOpen(false)}
                  className="p-1 rounded-md text-neutral-400 hover:text-white hover:bg-white/10 transition"
                ><X className="h-4 w-4" /></button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3.5 py-3.5 bg-gradient-to-b from-neutral-50 to-white">
            {voiceMode && (
              <VoiceStatusPill
                recording={recording}
                transcribing={transcribing}
                sending={sending}
              />
            )}
            {messages.length === 0 && !sending && (
              <div className="text-sm text-neutral-600 space-y-2.5" data-testid="assistant-empty-state">
                <p className="text-[14px] leading-snug text-neutral-900 font-semibold">
                  Hi, I'm Morgan.
                </p>
                <p className="text-[12.5px] leading-relaxed text-neutral-600">
                  Ask about the CPG Institute, apply course guidance, or work
                  with your own brand knowledge.
                </p>
                <p className="text-[10.5px] leading-relaxed text-neutral-500 pt-2 border-t border-neutral-100">
                  I ground every answer in approved sources — I don't make up
                  facts, send messages, or take actions.
                </p>
                <Link
                  to="/account/knowledge"
                  onClick={() => setOpen(false)}
                  data-testid="assistant-manage-kb-link"
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary hover:text-fuchsia-800 transition"
                >
                  <PenLine className="h-3 w-3" />
                  Manage brand knowledge Morgan can use →
                </Link>
                {isReviewer && (
                  <Link
                    to="/admin/account-assistant-review"
                    onClick={() => setOpen(false)}
                    data-testid="assistant-review-history-link"
                    className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-semibold text-neutral-600 hover:text-primary transition"
                  >
                    <MessageCircle className="h-3 w-3" />
                    View feedback history & promote to KB →
                  </Link>
                )}
                {threads.length > 0 && (
                  <div className="pt-2.5 mt-2.5 border-t border-neutral-100">
                    <div className="text-[9px] uppercase tracking-widest font-bold text-neutral-500 mb-1">
                      Recent conversations
                    </div>
                    <ul className="space-y-0.5">
                      {threads.slice(0, 6).map((t) => (
                        <li key={t.id}>
                          <button
                            data-testid={`assistant-thread-${t.id}`}
                            onClick={() => openThread(t.id)}
                            className="text-left text-[12px] w-full truncate px-2 py-1 rounded-md hover:bg-neutral-100 text-neutral-700 hover:text-neutral-900 transition"
                          >
                            {t.title || "Untitled conversation"}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {messages.map((m) => (
              <MessageBubble
                key={m.id}
                m={m}
                isReviewer={isReviewer}
                autoplayVoice={autoplayVoice}
                voiceModeActive={voiceMode}
                voiceSlug={voiceSlug}
                onSpokenReplyStart={(audioEl) => bargeStartRef.current?.(audioEl)}
                onSpokenReplyEnd={() => {
                  // Only re-arm when we're still in a voice conversation
                  // (user has been driving with the mic this session).
                  bargeStopRef.current?.();
                  if (voiceModeRef.current && autoplayVoice) {
                    startRecordRef.current?.();
                  }
                }}
              />
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-xs text-neutral-500 mt-3" data-testid="assistant-loading">
                <span className="inline-block w-2 h-2 bg-pink-500 rounded-full animate-pulse" />
                <span className="inline-block w-2 h-2 bg-pink-500 rounded-full animate-pulse [animation-delay:120ms]" />
                <span className="inline-block w-2 h-2 bg-pink-500 rounded-full animate-pulse [animation-delay:240ms]" />
                <span>Morgan is thinking…</span>
              </div>
            )}
            {error && (
              <div className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 px-2 py-1 rounded" data-testid="assistant-error">
                {error}
              </div>
            )}
          </div>

          {/* Composer — pill-shaped, mic + textarea + send share one shell */}
          <form onSubmit={send} className="border-t border-neutral-200 px-3 py-3 bg-white/95 backdrop-blur-sm">
            {micError && (
              <div className="mb-1.5 text-[11px] text-red-600" data-testid="assistant-mic-error">
                {micError}
              </div>
            )}
            {recording && (
              <div className="mb-1.5 flex items-center gap-2 text-[11px] text-red-600" data-testid="assistant-recording">
                <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                Recording — tap the square to stop, or the X to cancel.
              </div>
            )}
            {transcribing && (
              <div className="mb-1.5 flex items-center gap-1.5 text-[11px] text-neutral-500" data-testid="assistant-transcribing">
                <Loader2 className="h-3 w-3 animate-spin" /> Transcribing…
              </div>
            )}
            <div className="flex items-end gap-1.5 rounded-full border border-neutral-200 bg-white pl-1.5 pr-1 py-1 shadow-[0_1px_2px_rgba(0,0,0,.04)] focus-within:border-pink-500 focus-within:ring-2 focus-within:ring-pink-500/25 transition">
              {/* Mic — tap-to-record; tap again to stop; X to cancel. */}
              {!recording ? (
                <button
                  type="button"
                  onClick={startRecord}
                  disabled={transcribing || sending}
                  data-testid="assistant-mic-btn"
                  aria-label="Record voice message"
                  title="Record voice message"
                  className="h-8 w-8 shrink-0 rounded-full grid place-items-center text-neutral-500 hover:text-pink-700 hover:bg-pink-50 disabled:opacity-40 transition"
                >
                  <Mic className="h-4 w-4" />
                </button>
              ) : (
                <div className="flex items-center gap-0.5">
                  <button
                    type="button"
                    onClick={stopMedia}
                    data-testid="assistant-mic-stop-btn"
                    aria-label="Stop recording"
                    title="Stop recording"
                    className="h-8 w-8 shrink-0 rounded-full bg-red-600 text-white grid place-items-center hover:bg-red-700 transition"
                  ><Square className="h-3.5 w-3.5" /></button>
                  <button
                    type="button"
                    onClick={cancelRecord}
                    data-testid="assistant-mic-cancel-btn"
                    aria-label="Cancel recording"
                    title="Cancel"
                    className="h-8 w-8 shrink-0 rounded-full grid place-items-center text-neutral-500 hover:text-red-700 hover:bg-red-50 transition"
                  ><X className="h-4 w-4" /></button>
                </div>
              )}
              <textarea
                ref={inputRef}
                data-testid="assistant-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                rows={1}
                placeholder="Ask Morgan…"
                aria-label="Ask Morgan"
                className="flex-1 resize-none bg-transparent border-0 focus:outline-none focus:ring-0 px-1 py-1.5 text-sm text-neutral-900 placeholder:text-neutral-400 max-h-32"
              />
              <button
                type="submit"
                data-testid="assistant-send-btn"
                disabled={sending || !input.trim()}
                className="h-8 w-8 shrink-0 rounded-full bg-gradient-to-br from-pink-500 to-fuchsia-600 text-white grid place-items-center disabled:opacity-40 disabled:from-neutral-300 disabled:to-neutral-400 hover:shadow-md hover:scale-[1.03] transition"
                aria-label="Send message"
              >
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

/**
 * Turn Morgan's raw text into something that reads cleanly in the
 * widget when the persistent config asks for `plain` formatting.
 * Removes bold/italic asterisk markers, heading hashes, leading
 * bullet dashes, and collapses runs of blank lines. Keeps paragraph
 * breaks (blank line between paragraphs). Idempotent for already-
 * clean prose. This is a safety net — the system prompt already
 * tells Morgan to reply in plain conversational prose.
 */
function _cleanMorganText(raw) {
  if (!raw) return "";
  let s = String(raw);
  // Strip **bold** and __bold__ (keep inner text)
  s = s.replace(/\*\*(.+?)\*\*/g, "$1").replace(/__(.+?)__/g, "$1");
  // Strip lone italic *word* / _word_ markers only when they wrap a word (avoid math)
  s = s.replace(/(^|\s)\*([^*\n]+?)\*(?=\s|$|[.,;:!?])/g, "$1$2");
  s = s.replace(/(^|\s)_([^_\n]+?)_(?=\s|$|[.,;:!?])/g, "$1$2");
  // Drop leading heading hashes on a line, keep the text
  s = s.replace(/^\s{0,3}#{1,6}\s+/gm, "");
  // Drop leading bullet dashes / asterisks / plus on a line (turn into paragraph)
  s = s.replace(/^\s{0,3}[-*+]\s+/gm, "");
  // Drop leading numeric list markers ("1. ")
  s = s.replace(/^\s{0,3}\d{1,2}[.)]\s+/gm, "");
  // Strip stray [SOURCE N] tags — they belong under the message, not inline
  s = s.replace(/\[SOURCE\s+\d+\]/gi, "");
  // Collapse >2 blank lines into a single blank line
  s = s.replace(/\n{3,}/g, "\n\n");
  return s.trim();
}





function MessageBubble({ m, isReviewer, autoplayVoice = false, voiceModeActive = false, voiceSlug = "warren", onSpokenReplyStart, onSpokenReplyEnd }) {
  const isUser = m.role === "user";
  const messageId = m.assistant_message_id || m.id;
  // Reviewers get thumbs UI only for real (persisted) assistant
  // messages — tmp / optimistic ids can't be reviewed.
  const canReview = isReviewer && !isUser
    && typeof messageId === "string"
    && !messageId.startsWith("tmp-")
    && !messageId.startsWith("asst-");

  const audioRef = useRef(null);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [ttsError, setTtsError]     = useState(null);
  const [isPlaying, setIsPlaying]   = useState(false);
  // Guarantees a single auto-play per received reply — even if React
  // re-renders the bubble mid-audio (e.g. thumbs feedback toggling).
  const autoplayFiredRef            = useRef(false);
  // Track in-flight TTS fetches so a hard stop / new message can
  // abort them before the blob even lands. Also ensures unmount
  // cancels rather than plays a stale reply after the user closed
  // the widget.
  const abortRef                    = useRef(null);

  const playTts = async () => {
    if (isPlaying) {
      // "Stop" click on the currently-playing bubble → clear the
      // singleton so the header pill hides too.
      try { audioRef.current?.pause(); } catch { /* noop */ }
      morganClearAudio(audioRef.current);
      audioRef.current = null;
      setIsPlaying(false);
      return;
    }
    if (!m.content) return;
    // Any prior clip (from a different bubble, from the voice picker,
    // etc.) MUST stop before we fetch — otherwise a slow network
    // could stack a new play() on top of the still-audible previous
    // clip. `morganRegisterAudio(null)` also updates the header pill.
    morganStopAllAudio();
    setTtsError(null);
    setTtsLoading(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const untrack = morganTrackAbort(ctrl);
    try {
      const r = await api.post("/account/assistant/voice/tts", {
        text: m.content.slice(0, 4000),
        assistant_message_id: (typeof messageId === "string" && !messageId.startsWith("tmp-") && !messageId.startsWith("asst-")) ? messageId : null,
        voice: voiceSlug,
      }, { responseType: "blob", signal: ctrl.signal });
      // If a newer play() aborted us while the network was in
      // flight, drop the response on the floor.
      if (ctrl.signal.aborted) return;
      const url = URL.createObjectURL(r.data);
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => {
        setIsPlaying(false);
        URL.revokeObjectURL(url);
        morganClearAudio(a);
        // Voice-conversation loop: only the freshly-received reply
        // (marked with `_autoplay`) triggers the re-arm. Manual
        // Read-aloud clicks on old bubbles never re-arm the mic.
        if (m?._autoplay) onSpokenReplyEnd?.();
      };
      a.onpause = () => {
        // TTS was interrupted (barge-in, manual Stop, or system).
        // Tear down the barge-in watcher — either way we're not
        // speaking anymore.
        setIsPlaying(false);
        morganClearAudio(a);
      };
      a.onerror = () => { setTtsError("Playback failed"); setIsPlaying(false); morganClearAudio(a); };
      // ── Interruption grace period ──────────────────────────────
      //   In voice mode, arm the barge-in watcher ~300 ms BEFORE
      //   Morgan starts speaking. This gives a quick "wait" or
      //   "actually…" — the moments right after a user pressed
      //   send and hears the reply landing — a real chance to
      //   catch the interrupt before any TTS audio is even audible.
      //   If we hear speech during the grace window, we skip play()
      //   entirely and go straight back to recording.
      const GRACE_MS = 300;
      let cancelledBeforeStart = false;
      if (m?._autoplay && voiceModeActive && onSpokenReplyStart) {
        // Give the watcher a temporary "audio surrogate" that, when
        // paused, sets our cancellation flag so we know not to play.
        const surrogate = {
          _paused: false,
          pause() { this._paused = true; cancelledBeforeStart = true; },
        };
        onSpokenReplyStart(surrogate);
        await new Promise((res) => setTimeout(res, GRACE_MS));
        if (cancelledBeforeStart) {
          // User barged in during the grace window — do not play.
          // The watcher already re-armed the recorder for us.
          setTtsLoading(false);
          URL.revokeObjectURL(url);
          setIsPlaying(false);
          return;
        }
      }
      // User intentionally clicked — safe to play immediately (no autoplay policy violation).
      // Register with the module-level singleton so any subsequent
      // play() call (from a different bubble, the voice picker, a
      // send, etc.) hard-stops this one before starting.
      morganRegisterAudio(a);
      await a.play();
      setIsPlaying(true);
      // Voice-mode only: while Morgan speaks, listen for the user to
      // interrupt. `onSpokenReplyStart` opens a listen-only mic stream
      // and pauses this audio element if it detects a voice spike.
      // We re-arm the watcher against the REAL audio element now that
      // playback is happening, so an interrupt actually pauses it.
      if (m?._autoplay && voiceModeActive) onSpokenReplyStart?.(a);
      // Fire-and-forget audit so admin voice-flag triage knows this
      // message was ever spoken. Never blocks playback.
      if (typeof messageId === "string" && !messageId.startsWith("tmp-")
          && !messageId.startsWith("asst-")) {
        api.post("/admin/account-assistant/voice-played",
                 { assistant_message_id: messageId }).catch(() => {});
      }
    } catch (err) {
      // AbortController.signal.abort() bubbles as CanceledError/
      // AbortError — that's a *successful* stop, not a failure to show.
      if (err?.name === "CanceledError" || err?.name === "AbortError" || ctrl.signal.aborted) {
        return;
      }
      setTtsError(err?.response?.data?.detail || err?.response?.status === 503 ? "Voice unavailable — text still works." : "Voice unavailable");
    } finally {
      untrack();
      abortRef.current = null;
      setTtsLoading(false);
    }
  };

  // Unmount safety: abort in-flight fetch and release ownership of the
  // singleton slot if this bubble still holds it. Prevents "ghost"
  // audio starting after the widget has already been torn down.
  //
  // React 18 Strict Mode wrinkle: in dev builds (which the preview env
  // runs) every component mounts → fake-unmounts → remounts. `useRef`
  // values are PRESERVED across the fake-unmount, so if we abort an
  // in-flight TTS fetch here without also resetting `autoplayFiredRef`,
  // the remount's autoplay effect short-circuits on
  // `if (autoplayFiredRef.current) return;` and the user hears nothing.
  // So: if the aborted fetch never got as far as creating an <audio>
  // element, clear the "autoplay already fired" guard so the second
  // mount can retry. If audio was already playing we leave the guard
  // in place — the component is actually tearing down and we don't
  // want a double-speak.
  useEffect(() => () => {
    const hadAudio = !!audioRef.current;
    try { abortRef.current?.abort(); } catch { /* noop */ }
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch { /* noop */ }
      morganClearAudio(audioRef.current);
      audioRef.current = null;
    }
    if (!hadAudio) {
      autoplayFiredRef.current = false;
    }
  }, []);

  // Auto-play the freshly-received reply exactly once. `_autoplay` is
  // set only by the send path (not by the thread-history loader) so
  // reopening a conversation never blasts the room with old audio.
  // The click that submitted the message counts as a user gesture,
  // so Chrome/Safari/Firefox all allow the immediate `.play()`.
  // NOTE: no `setTimeout` + cleanup — React 18 strict-mode's dev-only
  // double-mount cancels timed effects and swallowed the first play.
  useEffect(() => {
    if (isUser) return;
    if (!autoplayVoice) return;
    if (!m?._autoplay) return;
    if (autoplayFiredRef.current) return;
    if (!m.content) return;
    autoplayFiredRef.current = true;
    playTts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m?._autoplay, autoplayVoice, isUser, m?.content]);

  return (
    <div className={`mt-3 flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${isUser ? "bg-primary text-white rounded-br-sm" : "bg-white text-neutral-900 border border-neutral-200 rounded-bl-sm"}`}>
        {(m.content ? _cleanMorganText(m.content) : (m.error ? "Morgan is temporarily unavailable — try again shortly." : ""))}
        {!isUser && m.content && (
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={playTts}
              disabled={ttsLoading}
              data-testid={`assistant-tts-btn-${messageId}`}
              aria-label={isPlaying ? "Stop playback" : "Play in Morgan's voice"}
              title={isPlaying ? "Stop" : "Play in Morgan's voice"}
              className="text-[11px] inline-flex items-center gap-1 text-neutral-500 hover:text-pink-700 disabled:opacity-40"
            >
              {ttsLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Volume2 className="h-3 w-3" />}
              {isPlaying ? "Stop" : "Read aloud"}
            </button>
            {ttsError && (
              <span className="text-[10px] text-red-500" data-testid={`assistant-tts-err-${messageId}`}>{ttsError}</span>
            )}
          </div>
        )}
        {!isUser && Array.isArray(m.citations) && m.citations.length > 0 && (
          <div className="mt-2 pt-2 border-t border-neutral-100 space-y-1">
            {m.citations.slice(0, 6).map((c, i) => {
              const src = (c.source || "global");
              const isShared = src === "global" || src === "platform";
              return (
                <div key={i} className="text-[11px] text-neutral-500 flex items-center gap-1 flex-wrap"
                     data-testid={`assistant-citation-${isShared ? "shared" : "account"}`}>
                  {isShared
                    ? <Book className="h-3 w-3 text-pink-600" />
                    : <UserIcon className="h-3 w-3 text-indigo-600" />}
                  <span className="truncate">{c.title || c.document_id}</span>
                </div>
              );
            })}
          </div>
        )}
        {canReview && <ReviewControls messageId={messageId} />}
      </div>
    </div>
  );
}


/**
 * Inline Good/Bad affordance shown only to admin/super_admin. A click
 * on 👍 saves immediately with no reason. A click on 👎 opens a
 * one-textarea form that captures the reason and (optionally) a
 * short proposed correction; both flow into the shared
 * `account_assistant_feedback` store via
 * `POST /api/admin/account-assistant/feedback`. From there they
 * appear in /admin/account-assistant-review — the "knowledge area"
 * where admins can review and (via that page) promote a good example
 * or a correction into the shared KB.
 */
function ReviewControls({ messageId }) {
  const [status, setStatus] = useState(null); // null | "good" | "bad" | "saved-good" | "saved-bad"
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const [reason, setReason] = useState("");
  const [correction, setCorrection] = useState("");

  const submit = async (verdict) => {
    setError(null);
    if (verdict === "bad" && !reason.trim()) {
      setStatus("bad");     // open the form; wait for a reason
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/account-assistant/feedback", {
        assistant_message_id: messageId,
        verdict,
        reason: verdict === "bad" ? reason.trim() : (reason.trim() || ""),
        proposed_correction: correction.trim(),
      });
      setStatus(`saved-${verdict}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (status === "saved-good" || status === "saved-bad") {
    return (
      <div className="mt-2 pt-2 border-t border-neutral-100 flex items-center gap-1.5 text-[10.5px] text-neutral-500"
           data-testid={`assistant-review-saved-${messageId}`}>
        <Check className="h-3 w-3 text-emerald-600" />
        {status === "saved-good" ? "Thanks — logged as a good example." : "Logged — thanks for the correction."}
        <Link to="/admin/account-assistant-review" className="ml-auto text-primary hover:underline font-semibold">
          view history
        </Link>
      </div>
    );
  }

  return (
    <div className="mt-2 pt-2 border-t border-neutral-100" data-testid={`assistant-review-controls-${messageId}`}>
      {status !== "bad" && (
        <div className="flex items-center gap-1.5">
          <span className="text-[10.5px] uppercase tracking-widest font-bold text-neutral-500">Admin review</span>
          <button
            type="button"
            onClick={() => submit("good")}
            disabled={saving}
            data-testid={`assistant-review-good-${messageId}`}
            className="ml-auto h-6 w-6 grid place-items-center rounded-full text-neutral-500 hover:text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 transition"
            aria-label="Good answer"
            title="Good answer"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <ThumbsUp className="h-3 w-3" />}
          </button>
          <button
            type="button"
            onClick={() => setStatus("bad")}
            disabled={saving}
            data-testid={`assistant-review-bad-${messageId}`}
            className="h-6 w-6 grid place-items-center rounded-full text-neutral-500 hover:text-rose-700 hover:bg-rose-50 disabled:opacity-40 transition"
            aria-label="Bad answer"
            title="Bad answer"
          >
            <ThumbsDown className="h-3 w-3" />
          </button>
        </div>
      )}
      {status === "bad" && (
        <div className="space-y-1.5" data-testid={`assistant-review-form-${messageId}`}>
          <div className="text-[10.5px] uppercase tracking-widest font-bold text-rose-700">Why is it bad?</div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Short reason — what's wrong?"
            data-testid={`assistant-review-reason-${messageId}`}
            className="w-full text-xs rounded-md border border-rose-200 px-2 py-1.5 focus:outline-none focus:border-rose-500 bg-white"
          />
          <textarea
            value={correction}
            onChange={(e) => setCorrection(e.target.value)}
            rows={2}
            placeholder="Optional — what should Morgan have said instead? (saved as a proposed correction)"
            data-testid={`assistant-review-correction-${messageId}`}
            className="w-full text-xs rounded-md border border-neutral-200 px-2 py-1.5 focus:outline-none focus:border-primary bg-white"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => submit("bad")}
              disabled={saving || !reason.trim()}
              data-testid={`assistant-review-submit-${messageId}`}
              className="text-[11px] px-2.5 py-1 rounded-md bg-rose-600 text-white disabled:opacity-40 hover:bg-rose-700 transition"
            >{saving ? "Saving…" : "Save feedback"}</button>
            <button
              type="button"
              onClick={() => { setStatus(null); setError(null); }}
              className="text-[11px] px-2 py-1 rounded-md text-neutral-500 hover:text-neutral-800 transition"
            >Cancel</button>
            <Link
              to="/admin/account-assistant-review"
              className="ml-auto text-[11px] text-primary hover:underline font-semibold"
            >history →</Link>
          </div>
          {error && <div className="text-[10.5px] text-rose-700">{error}</div>}
        </div>
      )}
    </div>
  );
}



// Persistent "call status" pill shown at the top of the message
// stream while voice mode is active. Mirrors phone-call feedback:
// Listening → Transcribing → Morgan is thinking → Morgan is speaking.
function VoiceStatusPill({ recording, transcribing, sending }) {
  let label = "Morgan is speaking…";
  let dot   = "bg-emerald-500";
  if (recording)         { label = "Listening…";              dot = "bg-red-500 animate-pulse"; }
  else if (transcribing) { label = "Getting your words…";     dot = "bg-pink-500 animate-pulse"; }
  else if (sending)      { label = "Morgan is thinking…";     dot = "bg-pink-500 animate-pulse"; }
  return (
    <div
      data-testid="assistant-voice-status"
      className="mb-3 inline-flex items-center gap-2 rounded-full bg-neutral-900 text-white px-3 py-1.5 text-[11px] font-semibold shadow-sm"
    >
      <span className={`w-2 h-2 rounded-full ${dot}`} />
      {label}
    </div>
  );
}


// ── VoicePicker ────────────────────────────────────────────────────
// Compact popover with per-row Play-sample buttons. Uses the same
// TTS endpoint the actual voice-out path uses, so previewing =
// exactly what the founder will hear when Morgan speaks a reply.
// One shared <audio> element is kept in a ref so tapping preview on
// row B correctly interrupts the sample still playing from row A.
function VoicePicker({ catalog, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [previewing, setPreviewing] = useState(null); // slug being previewed
  const audioRef = useRef(null);
  const wrapRef = useRef(null);

  const active = catalog.find((v) => v.slug === value) || catalog[0];

  // Outside-click closes the popover so it feels native.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Stop any sample when the popover closes or unmounts so audio
  // doesn't leak out of the widget.
  useEffect(() => {
    if (open) return;
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch { /* noop */ }
      morganClearAudio(audioRef.current);
      audioRef.current = null;
    }
    setPreviewing(null);
  }, [open]);

  const preview = async (slug, ev) => {
    ev.stopPropagation();
    // Toggle-off if the same row is already playing.
    if (previewing === slug && audioRef.current) {
      try { audioRef.current.pause(); } catch { /* noop */ }
      morganClearAudio(audioRef.current);
      audioRef.current = null;
      setPreviewing(null);
      return;
    }
    // Kill EVERY Morgan clip currently in the air (main-thread TTS
    // reply, another preview row, etc.) so previews never stack.
    morganStopAllAudio();
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch { /* noop */ }
      audioRef.current = null;
    }
    setPreviewing(slug);
    try {
      const r = await api.post("/account/assistant/voice/tts", {
        text: "Hi, I'm Morgan. This is a quick sample of my voice so you can pick the one you like best.",
        voice: slug,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => {
        setPreviewing(null);
        URL.revokeObjectURL(url);
        morganClearAudio(a);
        audioRef.current = null;
      };
      a.onpause = () => {
        setPreviewing(null);
        morganClearAudio(a);
      };
      a.onerror = () => { setPreviewing(null); morganClearAudio(a); audioRef.current = null; };
      morganRegisterAudio(a);
      await a.play();
    } catch {
      setPreviewing(null);
      audioRef.current = null;
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-label="Choose Morgan's voice"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="assistant-voice-select"
        title={disabled ? "Turn voice on to pick" : `Voice: ${active?.label}`}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold bg-white/10 hover:bg-white/20 text-white rounded-full pl-2.5 pr-1.5 py-1 border border-white/10 outline-none focus:ring-1 focus:ring-white/30 disabled:opacity-40 disabled:cursor-not-allowed transition max-w-[9rem]"
      >
        <span className="truncate">{active?.label || "Voice"}</span>
        <ChevronDown className="h-3 w-3 shrink-0" />
      </button>

      {open && (
        <div
          role="listbox"
          data-testid="assistant-voice-popover"
          className="absolute right-0 mt-2 w-64 max-h-[70vh] overflow-y-auto rounded-xl border border-white/10 bg-neutral-900/95 backdrop-blur-md shadow-2xl z-50 py-1"
        >
          {catalog.map((v) => {
            const selected = v.slug === value;
            const isPlaying = previewing === v.slug;
            return (
              <div
                key={v.slug}
                role="option"
                aria-selected={selected}
                onClick={() => { onChange(v.slug); setOpen(false); }}
                data-testid={`assistant-voice-option-${v.slug}`}
                className={`flex items-start gap-2 px-3 py-2 cursor-pointer transition ${selected ? "bg-white/10" : "hover:bg-white/5"}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold text-white truncate">{v.label}</span>
                    {selected && <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />}
                  </div>
                  {v.description && (
                    <div className="text-[11px] text-white/60 leading-snug mt-0.5">{v.description}</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={(ev) => preview(v.slug, ev)}
                  aria-label={`Play sample of ${v.label}`}
                  data-testid={`assistant-voice-preview-${v.slug}`}
                  className="shrink-0 mt-0.5 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
                  title="Play sample"
                >
                  {isPlaying
                    ? <Square className="h-3 w-3" />
                    : <Play className="h-3 w-3" />}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
